import { ReactNode, useEffect, useMemo } from 'react';
import { usePermissions } from '@/hooks/use-permissions';
import { NoPermissionPage } from '@/components/no-permission-page';
import { useNavigate, useLocation } from 'react-router-dom';

type PermissionType = 'page' | 'show';
interface PermissionGuardProps {
  /** 权限标识 */
  permission?: string;
  /** 子组件 */
  children: ReactNode;
  /** 无权限页面类型 */
  type?: PermissionType;
  /** 无权限时显示的内容（可选，会覆盖type的默认行为） */
  fallback?: ReactNode;
  /** 无权限页面标题 */
  noPermissionTitle?: string;
  /** 无权限页面描述 */
  noPermissionDescription?: string;
}

/**
 * 权限控制组件
 * 用于条件渲染基于权限的内容
 * 默认展示有权限内容，只有在明确无权限时才显示无权限页面
 */
export const PermissionGuard = ({
  permission,
  children,
  type = 'page',
  fallback,
  noPermissionTitle,
  noPermissionDescription,
}: PermissionGuardProps) => {
  const { hasPermission, agentPermissions } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();

  // 权限是否已加载
  const permissionsLoaded = !!agentPermissions?.mPerms;

  // 仅在权限已加载后才计算 hasAccess，避免不必要的闪烁
  const hasAccess = useMemo(() => {
    if (!permission) return true; // 未传入权限则直接放行
    if (!permissionsLoaded) return true; // 未加载前乐观渲染
    return hasPermission(permission);
  }, [permission, permissionsLoaded, hasPermission]);

  // 禁止在渲染期间导航：当确定无权限时再跳转
  useEffect(() => {
    if (!permission) return; // 未传入权限不做跳转
    if (!permissionsLoaded) return;
    if (!hasAccess && type === 'page' && location.pathname !== '/home') {
      navigate('/home', { replace: true });
    }
  }, [permission, permissionsLoaded, hasAccess, type, location.pathname, navigate]);

  // 如果权限数据还未加载完成，默认显示有权限内容（乐观渲染）
  if (!permissionsLoaded) {
    return <>{children}</>;
  }

  // 有权限，直接显示内容
  if (hasAccess) {
    return <>{children}</>;
  }

  // 无权限时，优先渲染自定义 fallback
  if (fallback) {
    return <>{fallback}</>;
  }

  // 显示默认的无权限页面或空内容
  const defaultTitle = noPermissionTitle || '没有权限';
  const defaultDescription = noPermissionDescription || '您没有访问此功能的权限';

  switch (type) {
    case 'show':
      return <div></div>;
    case 'page':
    default:
      return <NoPermissionPage title={defaultTitle} description={defaultDescription} />;
  }
};
